import _ from 'lodash';
import App from '@gral/app';
import { WrapperEntitySelector } from '@components/Inputs';
import * as Misc from '@components/Misc';
import React, { useEffect, useRef } from 'react';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { useAppDispatch } from '@redux/store';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import { EAclAction, IForm } from '@ts/ui-models';
import { CLayer } from '@models/maps/Layer';
import { TMode } from '@ts/ui-models';
import { Grid, TextInput, Typography } from '@ts/storybook';

interface ILayerFormProps extends IForm {
  values: CLayer;
}

const LayerForm = ({ values, mode, requiredFields, onChange, onCallbackChange }: ILayerFormProps) => {
  const dispatch = useAppDispatch();
  const { name, backgroundId } = values;

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isNewMode = mode === 'new';

  const [linkedRulesets, setLinkedRulesets] = React.useState<string[]>([]);

  const firstLinkeds = useRef<string[]>([]); // Ref to store the initially linked items

  //#region Handlers

  const _handleChangeEntitySelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    onChange(entityName, entity);
    onChange(fkField, value);
  };

  const onLinkRuleset = (ruleset: string[]) => {
    // Find newly added rulesets: present in `ruleset` but not in `firstLinkeds.current`
    const newRulesets = ruleset.filter(id => !firstLinkeds.current.includes(id));

    // Find rulesets to unlink: present in `firstLinkeds.current` but no longer in `ruleset`
    const rulesetsToUnlink = firstLinkeds.current.filter(id => !ruleset.includes(id));

    // Update the rulesets that are newly linked
    onChange('rulesetsToLink', newRulesets);

    // Update the rulesets that need to be unlinked
    onChange('rulesetsToUnlink', rulesetsToUnlink);

    // Update the state with the currently linked rulesets
    setLinkedRulesets(ruleset);
  };
  //#endregion

  useEffect(() => {
    const loadRulesets = async () => {
      const requestRulesLinked = await dispatch(
        fetchData({
          url: '/api/rulesetsLinked',
          dataBody: { layerId: values.getID() },
        })
      );
      if (requestRulesLinked.payload?.data) {
        setLinkedRulesets(requestRulesLinked.payload.data);
        firstLinkeds.current = requestRulesLinked.payload.data.map(v => v.logicalId);
      }
    };
    loadRulesets();
  }, []);

  return (
    <Grid container spacing={2} mt={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Misc.NewPaperWithPadding>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="h6" text="Main information" />
            </Grid>
            <Grid size={12}>
              <TextInput
                label="Name"
                value={name}
                onChangeValue={onCallbackChange('name')}
                required={requiredFields.includes('name')}
                isReadOnly={isViewMode}
                infoText="Blank spaces at the beggining and the end will be trimmed on save"
              />
            </Grid>
            {!(!backgroundId && isViewMode) && (
              <Grid size={12}>
                <WrapperEntitySelector
                  label={'Background'}
                  searchComponent={generateSearchEntitySelector('document')}
                  value={backgroundId}
                  onChangeEntity={entity => _handleChangeEntitySelector(entity, 'background', 'backgroundId')}
                  isReadOnly={isViewMode}
                />
              </Grid>
            )}
            <Grid size={12}>
              <WrapperEntitySelector
                label={isNewMode ? ['To link ruleset first save'] : 'Link a Ruleset'}
                value={linkedRulesets}
                searchComponent={generateSearchEntitySelector('ruleset')}
                disabled={!isEditMode}
                isReadOnly={!isEditMode || !App.cxStore.checkPermission('linkRulesetToLayer', EAclAction.CREATE)}
                onChange={v => onLinkRuleset(v as string[])}
                multiple
                seeAllSelected
              />
            </Grid>
          </Grid>
        </Misc.NewPaperWithPadding>
      </Grid>
    </Grid>
  );
};

export default LayerForm;
